import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../config/api";
import axios from "axios";
import {
  Shield, Scale, FolderOpen, Receipt, CreditCard, Banknote,
  FileSpreadsheet, FileX, User, Loader2, Clock, ChevronRight,
  PoundSterling, Zap,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { getUserRole, ROLES } from "../config/roleConfig";
import { clientDisplayName } from "../utils/clientDisplayName";

const ROLE_CARD_SECTIONS = {
  [ROLES.ADMIN]:          ["compliance", "accounts", "client"],
  [ROLES.DIRECTOR]:       ["compliance", "accounts", "client"],
  [ROLES.RESI_FE]:        ["compliance", "accounts", "client"],
  [ROLES.RESI_ASSISTANT]: ["compliance", "accounts", "client"],
  [ROLES.COMM_PROP]:      ["compliance", "accounts", "client"],
  [ROLES.COCO_LIT]:       ["compliance", "accounts", "client"],
  [ROLES.POST_SCANNING]:  [],
  [ROLES.ACCOUNTS]:       ["compliance", "accounts", "client"],
};

function getCardSectionsForRole(email) {
  const role = getUserRole(email);
  if (!role) return ["compliance", "client"];
  return ROLE_CARD_SECTIONS[role] || ["compliance", "client"];
}

const FORM_TYPE_LABELS = {
  new_matter: "New Matter",
  invoice_request: "Invoice Request",
  payment_request: "Payment Request",
  file_closure: "File Closure",
  conflict_check: "Conflict Check",
  accounts_instruction: "Completion Funds",
  risk_assessment: "Risk Assessment",
  pre_caseminder: "Pre-CaseMinder",
};

const FORM_TYPE_COLORS = {
  new_matter: "text-status-amber",
  invoice_request: "text-teal",
  payment_request: "text-teal",
  file_closure: "text-cyan-300",
  conflict_check: "text-status-amber",
  accounts_instruction: "text-teal",
  risk_assessment: "text-status-amber",
  pre_caseminder: "text-status-amber",
};

export default function ActionsPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const userStr = localStorage.getItem("user");
  const userObj = userStr ? JSON.parse(userStr) : null;
  const userEmail = (
    userObj?.user_email ||
    userObj?.email ||
    userObj?.ms_email ||
    localStorage.getItem("bonsai_user_email") ||
    sessionStorage.getItem("bonsai_user_email") ||
    ""
  ).toLowerCase().trim();

  const visibleCardSections = getCardSectionsForRole(userEmail);

  useEffect(() => {
    (async () => {
      try {
        const [subRes, ccRes] = await Promise.all([
          axios.get(`${API}/submissions`),
          axios.get(`${API}/conflict-checks/recent?limit=50`),
        ]);
        const conflictChecks = (ccRes.data.conflict_checks || [])
          .filter(cc => !cc.is_test && !cc.searched_name?.startsWith("TEST_") && !cc.client_name?.startsWith("TEST_"))
          .map(cc => ({
            id: cc.id,
            form_type: "conflict_check",
            primary_label: clientDisplayName(cc) || cc.searched_name || "Unknown",
            secondary_label: cc.conflict_status === "cleared"
              ? `Cleared — ${cc.matter_reference || cc.provisional_id || "No ID"}`
              : `Status: ${cc.conflict_status || "pending"}`,
            tertiary_label: cc.searched_by_name || cc.decision_maker_name || "",
            created_at: cc.created_at,
            matter_reference: cc.matter_reference || cc.provisional_id,
          }));
        setSubmissions([...subRes.data, ...conflictChecks]);
      } catch (err) {
        console.error("Failed to fetch submissions:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const recentSubmissions = [...submissions]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 20);

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center gap-2.5 mb-6">
        <Zap className="w-5 h-5 text-teal" />
        <h1 className="text-xl font-bold text-white">Actions</h1>
      </div>

      {/* New Request Cards */}
      {visibleCardSections.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-white mb-5">New Request</h2>

          {visibleCardSections.includes("compliance") && (
            <>
              <p className="text-xs font-bold uppercase tracking-wider text-status-amber mb-2.5 flex items-center gap-1.5">
                <Scale className="w-3.5 h-3.5" />
                Compliance &amp; Golden Rule
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {[
                  { title: "Conflicts Search", desc: "Check before opening matter", href: "/conflicts-search", icon: Shield, testid: "action-conflicts-search" },
                  { title: "FORF & Risk Assessment", desc: "New matter file opening & CCL", href: "/new-matter", icon: Scale, testid: "action-new-matter" },
                  { title: "Pre-CaseMinder", desc: "Existing matters for CCL & conflicts", href: "/pre-caseminder", icon: FolderOpen, testid: "action-pre-caseminder" },
                  { title: "Risk Assessment", desc: "Mid-point or standalone review", href: "/risk-assessment", icon: Shield, testid: "action-risk-assessment" },
                ].map(card => (
                  <button key={card.testid} onClick={() => navigate(card.href)} className="group w-full bg-navy-card border border-white/[0.07] hover:border-amber-400/40 p-4 rounded-md text-left flex items-center gap-3.5 transition-all hover:-translate-y-0.5" style={{ borderLeft: "3px solid #f59e0b" }} data-testid={card.testid}>
                    <card.icon className="w-5 h-5 text-status-amber flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-status-amber block truncate">{card.title}</span>
                      <span className="text-xs text-txt-muted block truncate mt-0.5">{card.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {visibleCardSections.includes("accounts") && (
            <>
              <p className="text-xs font-bold uppercase tracking-wider text-teal mb-2.5 flex items-center gap-1.5">
                <PoundSterling className="w-3.5 h-3.5" />
                Accounts &amp; Finance
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {[
                  { title: "Invoice Request", desc: "Request invoice from Accounts", href: "/invoice-request", icon: Receipt, testid: "action-invoice" },
                  { title: "Payment Request", desc: "Single payment from Accounts", href: "/payment-request", icon: CreditCard, testid: "action-payment" },
                  // Brief 11c: Completion Funds soft-deleted. accounts_instruction
                  // label retained in the LABEL_MAP above (line 37) so historic
                  // submissions still render correctly in the recent-activity
                  // panel + detail views.
                  { title: "Third-Party Invoices", desc: "Upload invoices for May to process", href: "/third-party-invoices", icon: FileSpreadsheet, testid: "action-third-party" },
                ].map(card => (
                  <button key={card.testid} onClick={() => navigate(card.href)} className="group w-full bg-navy-card border border-white/[0.07] hover:border-teal/40 p-4 rounded-md text-left flex items-center gap-3.5 transition-all hover:-translate-y-0.5" style={{ borderLeft: "3px solid var(--brand)" }} data-testid={card.testid}>
                    <card.icon className="w-5 h-5 text-teal flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-teal block truncate">{card.title}</span>
                      <span className="text-xs text-txt-muted block truncate mt-0.5">{card.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {visibleCardSections.includes("client") && (
            <>
              <p className="text-xs font-bold uppercase tracking-wider text-cyan-300 mb-2.5 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                Client &amp; Matter
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {[
                  { title: "File Closure", desc: "Risk checks & notify client care", href: "/file-closure", icon: FileX, testid: "action-file-closure" },
                ].map(card => (
                  <button key={card.testid} onClick={() => navigate(card.href)} className="group w-full bg-navy-card border border-white/[0.07] hover:border-blue-400/40 p-4 rounded-md text-left flex items-center gap-3.5 transition-all hover:-translate-y-0.5" style={{ borderLeft: "3px solid #3b82f6" }} data-testid={card.testid}>
                    <card.icon className="w-5 h-5 text-cyan-300 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-cyan-200 block truncate">{card.title}</span>
                      <span className="text-xs text-txt-muted block truncate mt-0.5">{card.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Recent Submissions */}
      <div>
        <h2 className="text-lg font-bold text-white mb-4">Recent Submissions</h2>
        {loading ? (
          <div className="flex items-center gap-2 text-txt-muted text-sm py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />Loading submissions...
          </div>
        ) : recentSubmissions.length === 0 ? (
          <p className="text-sm text-txt-muted py-8 text-center">No submissions yet.</p>
        ) : (
          <div className="bg-navy-card border border-navy-border rounded-md overflow-hidden">
            {recentSubmissions.map((sub) => {
              const typeLabel = FORM_TYPE_LABELS[sub.form_type] || sub.form_type;
              const typeColor = FORM_TYPE_COLORS[sub.form_type] || "text-txt-muted";
              const dateStr = sub.created_at
                ? format(parseISO(sub.created_at), "dd MMM yyyy HH:mm")
                : "";
              const href = sub.form_type === "conflict_check"
                ? `/conflict-check/${sub.id}`
                : `/submission/${sub.id}`;

              return (
                <button
                  key={sub.id}
                  onClick={() => navigate(href)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-b border-navy-border last:border-0 hover:bg-navy-hover transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-white block truncate">
                      {sub.client_display_name || sub.primary_label || sub.matter_reference || "Untitled"}
                    </span>
                    <span className="text-xs text-txt-muted block truncate mt-0.5">
                      {sub.secondary_label || ""}
                    </span>
                  </div>
                  <span className={`text-xs font-medium shrink-0 ${typeColor}`}>{typeLabel}</span>
                  <span className="text-[10px] text-txt-faint shrink-0 flex items-center gap-1">
                    <Clock className="w-3 h-3" />{dateStr}
                  </span>
                  <ChevronRight className="w-4 h-4 text-txt-faint shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
